/**
 * PIKA-780 — weekend-aware position and width maths for Timeline items.
 *
 * This module is deliberately free of app imports. `canvas_timeline_util.js`
 * cannot be unit tested because importing it drags in DataManager,
 * ComposeManager, ProjectGroup and a circular import of the canvas_timeline
 * component — which is why `canvas_timeline.test.ts` is `describe.skip` and why
 * the position maths had no coverage. Keeping the arithmetic here lets it be
 * tested directly, and lets any weekend indicator read one descriptor instead of
 * re-deriving the maths.
 *
 * Canvas dates are integer day indices where 0 is 1 Jan 2000, a Saturday. That
 * is the same origin `getOutOfWeekendPadding` assumes with its `(date - 2) % 7`,
 * and CANVAS_DATE_DAYS_OFFSET_FIRST_WEEK = 2 is the offset of the first Monday.
 */

/** Offset of the first Monday from canvas date 0. Mirrors CANVAS_DATE_DAYS_OFFSET_FIRST_WEEK. */
const FIRST_MONDAY_OFFSET = 2;

/** Index of Saturday once a date is normalised to 0 = Monday. */
const FIRST_WEEKEND_INDEX = 5;

/** Longest run of consecutive weekend days, used to bound the boundary scans. */
const MAX_CONSECUTIVE_WEEKEND_DAYS = 2;

/**
 * The width that suppresses a bar: `drawGroupItems` only paints when
 * `width >= 0`. An item with no visible day has no column to occupy, so it must
 * keep suppressing the bar — a width of 0 would paint a zero-width sliver on the
 * following Monday, a date the item does not have. Making such a task findable
 * is the weekend notice's job, not the bar's.
 */
export const NO_VISIBLE_DAYS_WIDTH = -1;

export type TimelineItemDates = {
	startDate: number;
	endDate: number;
};

export type WeekendGeometry = {
	/** Pixel offset of the item's first visible day from the timeline start. */
	x: number;
	/** Pixel width covering only the days the item actually occupies. Never negative. */
	width: number;
	/**
	 * False when no bar and no hit area should exist: the item has no visible day,
	 * or its width rounds below zero, which is how the original maths suppressed a
	 * bar. Both branches apply the same rule.
	 */
	isDrawable: boolean;
	/** The item's real start date falls on a weekend that is currently hidden. */
	hiddenStart: boolean;
	/** The item's real end date falls on a weekend that is currently hidden. */
	hiddenEnd: boolean;
	/** The item occupies hidden weekend days exclusively. */
	hiddenOnly: boolean;
	/** First day of the item that has a column, or null when it has none. */
	firstVisibleDate: number | null;
	/** Last day of the item that has a column, or null when it has none. */
	lastVisibleDate: number | null;
};

/** Normalise a canvas date to 0 = Monday … 6 = Sunday. Safe for dates before the origin. */
export const getDayOfWeekIndex = (date: number): number => (((Math.floor(date) - FIRST_MONDAY_OFFSET) % 7) + 7) % 7;

/** True when the given canvas date is a Saturday or a Sunday. */
export const isWeekendDate = (date: number): boolean => getDayOfWeekIndex(date) >= FIRST_WEEKEND_INDEX;

/**
 * Weekend days in the half-open range [from, to).
 *
 * Signed: when `to` precedes `from` the count of the reversed range is negated,
 * so callers can offset items that begin before the timeline start without a
 * separate "days until timeline" term.
 */
export const countWeekendDaysBetween = (from: number, to: number): number => {
	const start = Math.floor(from);
	const end = Math.floor(to);

	if (start === end) return 0;
	if (end < start) return -countWeekendDaysBetween(end, start);

	const totalDays = end - start;
	// Every whole week contributes exactly one Saturday and one Sunday.
	let weekendDays = Math.floor(totalDays / 7) * 2;

	const remainderStart = start + Math.floor(totalDays / 7) * 7;
	for (let date = remainderStart; date < end; date++) {
		if (isWeekendDate(date)) weekendDays++;
	}

	return weekendDays;
};

/**
 * Days that have a column between [from, to). With weekends hidden this is the
 * calendar distance minus the weekend days that were removed from the axis.
 */
export const countVisibleDaysBetween = (from: number, to: number, hideWeekends: boolean): number => {
	const calendarDays = Math.floor(to) - Math.floor(from);
	return hideWeekends ? calendarDays - countWeekendDaysBetween(from, to) : calendarDays;
};

/**
 * First day of the item that has a column, or null when every day is hidden.
 * Only meaningful with weekends hidden; with them shown every day has a column,
 * and getWeekendGeometry returns before reaching here.
 */
const findFirstVisibleDate = (item: TimelineItemDates): number | null => {
	const start = Math.floor(item.startDate);
	const end = Math.floor(item.endDate);

	for (let offset = 0; offset <= MAX_CONSECUTIVE_WEEKEND_DAYS; offset++) {
		const date = start + offset;
		if (date > end) return null;
		if (!isWeekendDate(date)) return date;
	}

	return null;
};

/**
 * The first date on or after `date` that has a column when weekends are hidden.
 * Saturday advances two days to Monday, Sunday one; a weekday is already visible.
 */
const getNextVisibleDate = (date: number): number => {
	const dayOfWeek = getDayOfWeekIndex(date);
	return dayOfWeek < FIRST_WEEKEND_INDEX ? date : date + (7 - dayOfWeek);
};

/** Last day of the item that has a column, or null when every day is hidden. See above. */
const findLastVisibleDate = (item: TimelineItemDates): number | null => {
	const start = Math.floor(item.startDate);
	const end = Math.floor(item.endDate);

	for (let offset = 0; offset <= MAX_CONSECUTIVE_WEEKEND_DAYS; offset++) {
		const date = end - offset;
		if (date < start) return null;
		if (!isWeekendDate(date)) return date;
	}

	return null;
};

/**
 * Everything the Timeline needs to draw an item, and everything a weekend
 * indicator needs to describe one.
 *
 * The bar is anchored to the item's first VISIBLE day rather than to its start
 * date. The old maths already landed on the same pixel — verified identical
 * across 1820 start/duration combinations — but only as a side effect of
 * `getWeekendDaysBefore` excluding the start day. Stating the anchor directly
 * keeps it true for the weekend-only case, where there is no visible day at all.
 *
 * The behaviour change is width: it counts visible days only and is clamped at
 * zero. A weekend-only task used to produce `-1`, so it was never painted and
 * could not be hovered, clicked or linked — the disappearance PIKA-780 is about.
 * `isDrawable` now carries that case explicitly instead of leaving it to a
 * negative number. `calculateItemWidth` still maps that case back to `-1`, so
 * the bar itself is unchanged; it is the notice that makes the task findable.
 *
 * `timelineStartDate` is NOT a whole day. The Timeline scrolls by sub-day
 * amounts (`this.startDate + deltaSeconds / SECONDS_PER_DAY`) and every other
 * date-to-pixel conversion in `canvas_timeline` keeps that fraction, so this one
 * must too — flooring it would park every bar on a whole-day boundary and make
 * it jump a full column at a time while the grid behind it scrolled smoothly.
 * The weekend counting still works off `Math.floor(timelineStartDate)`, exactly
 * as `getWeekendDaysBefore` did, and the fraction is subtracted afterwards.
 */
export const getWeekendGeometry = (
	item: TimelineItemDates,
	timelineStartDate: number,
	pixelsPerDay: number,
	hideWeekends: boolean
): WeekendGeometry => {
	const start = Math.floor(item.startDate);
	const end = Math.floor(item.endDate);
	const timelineStartFraction = timelineStartDate - Math.floor(timelineStartDate);

	if (!hideWeekends) {
		// Keep the original arithmetic exactly: unfloored dates, so a fractional
		// item date still yields a fractional width, and a negative result — which
		// an item with endDate < startDate produces — keeps suppressing the bar
		// through isDrawable, as the old -1 width did.
		const rawWidth = (item.endDate - item.startDate + 1) * pixelsPerDay - 1;

		return {
			x: (item.startDate - timelineStartDate) * pixelsPerDay,
			width: Math.max(0, rawWidth),
			isDrawable: rawWidth >= 0,
			hiddenStart: false,
			hiddenEnd: false,
			hiddenOnly: false,
			firstVisibleDate: start,
			lastVisibleDate: end,
		};
	}

	const firstVisibleDate = findFirstVisibleDate(item);
	const lastVisibleDate = findLastVisibleDate(item);
	const hiddenOnly = firstVisibleDate === null || lastVisibleDate === null;

	// With no visible day there is no bar. Anchor the descriptor on the next day
	// that does have a column so an indicator has somewhere to sit, but report
	// zero width and isDrawable false so nothing is painted on a date the item
	// does not occupy.
	const anchorDate = hiddenOnly ? getNextVisibleDate(start) : (firstVisibleDate as number);

	const visibleDays = hiddenOnly
		? 0
		: countVisibleDaysBetween(firstVisibleDate as number, (lastVisibleDate as number) + 1, true);
	// Same rule as the weekends-shown branch: a width the old maths made negative
	// -- one visible day at a zoom of under a pixel per day -- suppressed the bar,
	// and must keep doing so rather than painting a zero-width one.
	const rawWidth = visibleDays * pixelsPerDay - 1;

	return {
		x: (countVisibleDaysBetween(Math.floor(timelineStartDate), anchorDate, true) - timelineStartFraction) * pixelsPerDay,
		width: Math.max(0, rawWidth),
		isDrawable: !hiddenOnly && rawWidth >= 0,
		hiddenStart: isWeekendDate(start),
		hiddenEnd: isWeekendDate(end),
		hiddenOnly,
		firstVisibleDate,
		lastVisibleDate,
	};
};

/** An item whose real dates reach into a weekend the Timeline is hiding. */
export type WeekendDatedItem<T> = {
	item: T;
	/** Real start date, whatever the bar shows. */
	startDate: number;
	/** Real end date, whatever the bar shows. */
	endDate: number;
	hiddenStart: boolean;
	hiddenEnd: boolean;
	/** No visible day at all, so no bar is drawn for it. */
	hiddenOnly: boolean;
};

/**
 * The items a weekend indicator needs to name, in timeline order.
 *
 * An item qualifies when one of its own boundary dates sits on a hidden weekend
 * — that is the date the user cannot see. An item that merely *spans* a weekend,
 * Friday to Monday, has both boundaries visible and is deliberately excluded:
 * the ticket requires those to behave exactly as they do today.
 *
 * Returns nothing at all when weekends are shown, so callers need no second guard.
 */
export const collectWeekendDatedItems = <T extends TimelineItemDates>(
	items: readonly T[],
	hideWeekends: boolean
): WeekendDatedItem<T>[] => {
	if (!hideWeekends) return [];

	return items
		.filter(item => isWeekendDate(item.startDate) || isWeekendDate(item.endDate))
		.map(item => {
			const startDate = Math.floor(item.startDate);
			const endDate = Math.floor(item.endDate);

			return {
				item,
				startDate,
				endDate,
				hiddenStart: isWeekendDate(startDate),
				hiddenEnd: isWeekendDate(endDate),
				// No first visible day means no last one either, so one scan settles it.
				hiddenOnly: findFirstVisibleDate(item) === null,
			};
		})
		.sort((a, b) => a.startDate - b.startDate || a.endDate - b.endDate);
};
